/**
 * Session context.
 *
 * The session itself lives in an httpOnly cookie the browser sends automatically — there
 * is no token in JavaScript to manage (SPEC §10). This only caches *who* the signed-in
 * instructor is, so the shell can greet them by name without a round trip per render.
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { ApiError, api, type Instructor } from './api.js';

interface SessionValue {
  instructor: Instructor | null;
  loading: boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const SessionContext = createContext<SessionValue | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [instructor, setInstructor] = useState<Instructor | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { instructor: found } = await api.me();
      setInstructor(found);
    } catch (err) {
      // A 401 is the normal signed-out state, not a failure worth surfacing.
      if (!(err instanceof ApiError) || err.status !== 401) console.error(err);
      setInstructor(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await api.signIn.signOut();
    } finally {
      // Clear locally regardless: if the request failed the cookie may still be gone, and
      // leaving a stale name on screen is worse than an extra sign-in.
      setInstructor(null);
    }
  }, []);

  const value = useMemo(
    () => ({ instructor, loading, refresh, signOut }),
    [instructor, loading, refresh, signOut],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionValue {
  const value = useContext(SessionContext);
  if (!value) throw new Error('useSession must be used inside <SessionProvider>');
  return value;
}
